import styled from '@emotion/styled';
import MUIDrawer from '@material-ui/core/Drawer';
import PropTypes from 'prop-types';
import ArrowBack from '@icon/ArrowBack';
import Close from '@icon/Close';
import { getImage } from '@utils/imageHelper';
import { breakpoints, Spacings } from '@styles/index';
import { noOp } from '@constants/empty';

const fontGrey = 'var(--color-text-primary)';

const StyledDrawer = styled(MUIDrawer)`
  &.MuiDrawer-root > .MuiPaper-root {
    background-color: ${({ $backgroundColor }) =>
      $backgroundColor || 'var(--color-base-0)'};
    background-image: ${({ $imageURL }) =>
      $imageURL && `url(${getImage($imageURL)})`};
    background-position: ${({ $imagePosition }) => $imagePosition};
    background-repeat: no-repeat;
    background-size: ${({ $imageSize }) => $imageSize};
    background-attachment: ${({ $backgroundAttachment }) =>
      $backgroundAttachment};
    display: grid;
    grid-template: 1fr ${({ $hasFooter }) => ($hasFooter ? 'max-content' : '')} / 1fr;
    @media (max-width: ${breakpoints.mobile}) {
      width: 100%;
    }
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(10, ${Spacings.SPACING_8B});
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-auto-rows: max-content;
  padding-left: ${Spacings.SPACING_12B};
  padding-top: ${Spacings.SPACING_6B};
  padding-right: 115px;
  overflow-x: hidden;
  overflow-y: auto;
  @media (max-width: ${breakpoints.mobile}) {
    padding-left: ${Spacings.SPACING_4B};
    padding-right: ${Spacings.SPACING_4B};
    overflow-x: initial;
    grid-template-columns: repeat(10, 1fr);
  }
  ${({ $login, $hideCloseButton }) => {
    return (
      !$hideCloseButton && `grid-template-rows: 60px ${$login ? '1fr' : ''};`
    );
  }}
`;

const CloseWrapper = styled.div`
  grid-column: 1/11;
  cursor: ${({ $disablePointerOnClose }) =>
    !$disablePointerOnClose ? 'pointer' : 'not-allowed'};
  @media (max-width: ${breakpoints.mobile}) {
    grid-column: 1/2;
    width: 24px;
    height: 24px;
  }
`;

const DrawerWithFooter = ({
  children,
  backgroundColor,
  drawerState,
  onClose,
  hideCloseButton,
  showBackButton,
  onPressBack,
  imageURL,
  imagePosition,
  imageSize,
  backgroundAttachment,
  disableBackdropClick,
  disableEscapeKeyDown,
  login,
  variant,
  className,
  footer,
  onScroll,
  disablePointerOnClose
}) => {
  return (
    <StyledDrawer
      anchor="right"
      open={drawerState}
      onClose={onClose}
      PaperProps={{ id: 'DrawerPaper' }}
      disableEscapeKeyDown={disableEscapeKeyDown}
      disableBackdropClick={disableBackdropClick}
      variant={variant}
      className={className}
      $hasFooter={!!footer}
      $backgroundColor={backgroundColor}
      $imageURL={imageURL}
      $imagePosition={imagePosition}
      $imageSize={imageSize}
      $backgroundAttachment={backgroundAttachment}
    >
      <ContentWrapper
        $login={login}
        $hideCloseButton={hideCloseButton}
        onScroll={onScroll}
      >
        {!hideCloseButton && (
          <CloseWrapper
            className="close-icon"
            $disablePointerOnClose={disablePointerOnClose}
          >
            <Close size="24px" onClick={onClose} color={fontGrey} />
          </CloseWrapper>
        )}
        {showBackButton && (
          <CloseWrapper>
            <ArrowBack size="24px" onClick={onPressBack} color={fontGrey} />
          </CloseWrapper>
        )}
        {children}
      </ContentWrapper>
      {footer}
    </StyledDrawer>
  );
};

export default DrawerWithFooter;

DrawerWithFooter.propTypes = {
  children: PropTypes.node.isRequired,
  backgroundColor: PropTypes.string,
  drawerState: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  hideCloseButton: PropTypes.bool,
  showBackButton: PropTypes.bool,
  onPressBack: PropTypes.func,
  imageURL: PropTypes.string,
  imagePosition: PropTypes.string,
  imageSize: PropTypes.string,
  backgroundAttachment: PropTypes.string,
  disableEscapeKeyDown: PropTypes.bool,
  disableBackdropClick: PropTypes.bool,
  login: PropTypes.bool,
  variant: PropTypes.string,
  className: PropTypes.string,
  footer: PropTypes.node,
  onScroll: PropTypes.func,
  disablePointerOnClose: PropTypes.bool
};

DrawerWithFooter.defaultProps = {
  backgroundColor: null,
  drawerState: false,
  hideCloseButton: false,
  showBackButton: false,
  onPressBack: null,
  imageURL: null,
  imagePosition: null,
  imageSize: null,
  backgroundAttachment: null,
  disableEscapeKeyDown: false,
  disableBackdropClick: false,
  login: false,
  variant: 'temporary',
  className: '',
  footer: null,
  onScroll: noOp,
  disablePointerOnClose: false
};
